import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, DollarSign, Smartphone, AlertCircle, Building2, TrendingUp, CreditCard, QrCode, Activity, CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/lib/auth";
import { AiUsageCard } from "@/components/AiUsageCard";

const COLORS = ["#E11D48", "#0284c7", "#8B5CF6", "#10B981", "#F59E0B", "#3B82F6"];
const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

const STATUS_META = {
  ok:    { label: "TUDO OK",    color: "emerald", icon: CheckCircle2,  dot: "bg-emerald-500" },
  warn:  { label: "ATENÇÃO",    color: "amber",   icon: AlertTriangle, dot: "bg-amber-500"   },
  error: { label: "CRÍTICO",    color: "rose",    icon: XCircle,       dot: "bg-rose-500"    },
};

function HealthMonitor() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try { const { data } = await api.get("/system/health"); setData(data); }
    catch { setData(null); }
    finally { setRefreshing(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // 30s polling
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <Card className="p-4 border border-slate-200 bg-white dark:bg-[#141416] dark:border-slate-700" data-testid="health-monitor-loading">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
          <Activity className="w-4 h-4 animate-pulse"/> Verificando saúde do sistema…
        </div>
      </Card>
    );
  }

  const meta = STATUS_META[data.status] || STATUS_META.warn;
  const Icon = meta.icon;
  const bgGradient = {
    emerald: "from-emerald-50 via-white to-emerald-50/40 border-emerald-200 dark:from-emerald-950 dark:via-[#141416] dark:to-[#141416] dark:border-emerald-700",
    amber:   "from-amber-50 via-white to-amber-50/40 border-amber-200 dark:from-amber-950 dark:via-[#141416] dark:to-[#141416] dark:border-amber-600",
    rose:    "from-rose-50 via-white to-rose-50/40 border-rose-300 dark:from-rose-950 dark:via-[#141416] dark:to-[#141416] dark:border-rose-600",
  }[meta.color];
  const txtColor = {
    emerald: "text-emerald-700 dark:text-emerald-400", amber: "text-amber-700 dark:text-amber-400", rose: "text-rose-700 dark:text-rose-400",
  }[meta.color];

  return (
    <Card className={`border-2 bg-gradient-to-br ${bgGradient} overflow-hidden`} data-testid="health-monitor">
      <div role="button" tabIndex={0} onClick={() => setOpen(o => !o)} onKeyDown={(e) => { if (e.key === "Enter") setOpen(o => !o); }} className="w-full p-4 text-left flex items-center gap-3 cursor-pointer" data-testid="health-monitor-toggle">
        <div className="relative">
          <div className={`w-3 h-3 rounded-full ${meta.dot} ${data.status === "ok" ? "animate-pulse" : ""}`}/>
          <div className={`absolute inset-0 w-3 h-3 rounded-full ${meta.dot} opacity-30 animate-ping`}/>
        </div>
        <Icon className={`w-5 h-5 ${txtColor}`}/>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-black uppercase tracking-widest text-slate-900 dark:text-white">
            Monitor de Saúde — <span className={txtColor}>{meta.label}</span>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-300 mt-0.5">
            {data.checks.length} verificações · Última: {new Date(data.checked_at).toLocaleTimeString("pt-BR")}
            {data.status !== "ok" && (
              <>  · <strong>{data.checks.filter(c => c.status !== "ok").length} item(ns)</strong> precisam de atenção</>
            )}
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); load(); }} className="p-1.5 rounded-lg hover:bg-white/60 dark:hover:bg-white/10" title="Atualizar agora" data-testid="health-monitor-refresh">
          <RefreshCw className={`w-4 h-4 text-slate-500 dark:text-slate-300 ${refreshing ? "animate-spin" : ""}`}/>
        </button>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 dark:text-slate-300"/> : <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-300"/>}
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-white/80 dark:border-slate-700 space-y-1.5" data-testid="health-monitor-details">
          {data.checks.map(c => {
            const m = STATUS_META[c.status] || STATUS_META.warn;
            const CheckIcon = m.icon;
            const cTxt = { emerald: "text-emerald-700 dark:text-emerald-400", amber: "text-amber-700 dark:text-amber-400", rose: "text-rose-700 dark:text-rose-400" }[m.color];
            return (
              <div key={c.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/70 border border-white dark:bg-slate-900 dark:border-slate-700" data-testid={`health-check-${c.id}`}>
                <div className={`w-2 h-2 rounded-full ${m.dot} mt-1.5 shrink-0`}/>
                <CheckIcon className={`w-4 h-4 ${cTxt} shrink-0 mt-0.5`}/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 dark:text-white">{c.label}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-300">{c.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

const Kpi = ({ label, value, sub, icon: Icon, color = "#0f172a", testid }) => (
  <Card className="p-4 border border-slate-200" data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="font-heading text-2xl md:text-3xl font-black mt-1" style={{ color }}>{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1 truncate">{sub}</div>}
      </div>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: color + "1a" }}>
        <Icon className="w-4 h-4" style={{ color }}/>
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/dashboard/stats").then((r) => setD(r.data)).catch(()=>{}); }, []);

  if (!d) return <div className="py-20 text-center text-slate-400">Carregando painel...</div>;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Painel Geral</h1>
        <p className="text-slate-500 mt-1">Visão executiva da sua academia em tempo real</p>
      </div>

      <HealthMonitor/>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Clientes ativos" value={d.active_total} icon={Users} color="#0f172a" testid="kpi-active-total"/>
        <Kpi label="Vendas do mês" value={money(d.sales_total)} icon={DollarSign} color="#10B981" testid="kpi-sales-total"/>
        <Kpi label="Vendas online" value={money(d.online_total)} sub={`${d.sales_total ? Math.round(d.online_total/d.sales_total*100) : 0}% do total`} icon={Smartphone} color="#E11D48" testid="kpi-online-total"/>
        <Kpi label="Contratos encerrados" value={d.expired_count} sub="Alunos para reengajar" icon={AlertCircle} color="#F59E0B" testid="kpi-expired"/>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-slate-200" data-testid="chart-active-by-unit">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-rose-600"/>Clientes ativos por unidade</h3>
          {d.active_by_unit?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.active_by_unit} layout="vertical">
                <CartesianGrid stroke="#f1f5f9"/>
                <XAxis type="number" tick={{fontSize: 11}}/>
                <YAxis dataKey="unit" type="category" width={130} tick={{fontSize: 11}}/>
                <Tooltip/>
                <Bar dataKey="count" fill="#E11D48" radius={[0,6,6,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="py-10 text-center text-slate-400 text-sm">Sem dados</div>}
        </Card>
        <Card className="p-5 border border-slate-200" data-testid="chart-new-by-unit">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600"/>Novos clientes por unidade (30 dias)</h3>
          {d.new_by_unit_30d?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.new_by_unit_30d} layout="vertical">
                <CartesianGrid stroke="#f1f5f9"/>
                <XAxis type="number" tick={{fontSize: 11}}/>
                <YAxis dataKey="unit" type="category" width={130} tick={{fontSize: 11}}/>
                <Tooltip/>
                <Bar dataKey="count" fill="#10B981" radius={[0,6,6,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="py-10 text-center text-slate-400 text-sm">Sem novos alunos no período</div>}
        </Card>
      </div>

      <Card className="p-5 border border-slate-200" data-testid="chart-sales-by-unit">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-600"/>Vendas por unidade (mês)</h3>
        {d.sales_by_unit?.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.sales_by_unit}>
              <CartesianGrid stroke="#f1f5f9"/>
              <XAxis dataKey="unit" tick={{fontSize: 11}}/>
              <YAxis tick={{fontSize: 11}} tickFormatter={(v)=>`R$${v}`}/>
              <Tooltip formatter={(v)=>money(v)}/>
              <Bar dataKey="amount" fill="#0284c7" radius={[6,6,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="py-10 text-center text-slate-400 text-sm">Sem vendas no mês</div>}
      </Card>

      {user?.role === "admin" && <AiUsageCard/>}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-slate-200" data-testid="chart-online-by-type">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Smartphone className="w-4 h-4 text-rose-600"/>Vendas Online — por método</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-100">
              <div className="flex items-center gap-1 text-xs text-rose-700 font-bold"><QrCode className="w-3 h-3"/>PIX</div>
              <div className="font-heading text-2xl font-black text-rose-600 mt-1">{money(d.online_by_method?.pix)}</div>
            </div>
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-1 text-xs text-blue-700 font-bold"><CreditCard className="w-3 h-3"/>Cartão</div>
              <div className="font-heading text-2xl font-black text-blue-600 mt-1">{money(d.online_by_method?.card)}</div>
            </div>
          </div>
          {d.online_by_source?.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={d.online_by_source} dataKey="amount" nameKey="label" cx="50%" cy="50%" outerRadius={70} label={(e)=>`${e.label}: ${money(e.amount)}`}>
                  {d.online_by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={(v)=>money(v)}/>
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="py-6 text-center text-slate-400 text-sm">Sem vendas online neste mês</div>}
        </Card>

        <Card className="p-5 border border-slate-200" data-testid="expired-list">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-amber-600"/>Contratos encerrados ({d.expired_count})</h3>
          {d.expired_students?.length ? (
            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {d.expired_students.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded hover:bg-slate-50 border-b border-slate-100" data-testid={`expired-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{s.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.unit_name} · Venceu {s.membership_end ? new Date(s.membership_end).toLocaleDateString("pt-BR") : "—"}</div>
                  </div>
                  {s.phone && (
                    <a href={`https://wa.me/55${String(s.phone).replace(/\D/g, "")}?text=${encodeURIComponent("Olá! Seu plano venceu — que tal renovar?")}`} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold uppercase tracking-wider hover:bg-emerald-100">
                      💬 WhatsApp
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : <div className="py-6 text-center text-slate-400 text-sm">🎉 Nenhum contrato encerrado</div>}
        </Card>
      </div>
    </div>
  );
}
